import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/components/common/SafeIcon';
import MessageItem from '@/components/chat/MessageItem';
import ChatInput from '@/components/chat/ChatInput';
import MidnightClock from '@/components/common/MidnightClock';
import TarotTable from '@/components/tarot/TarotTable';
import QuotaIndicator from '@/components/chat/QuotaIndicator';
import { seats } from '@/data/characters';
import { chatService } from '@/services/api';
import { useAuthContext } from '@/components/auth/AuthProvider';
import { saveConversation, loadConversation } from '@/services/conversationStorage';

const { FiChevronLeft } = FiIcons;

export default function Chat() {
  const { charId } = useParams();
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const { user } = useAuthContext();
  const character = seats.find((s) => s.id === charId) || seats[0];

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showTarotTable, setShowTarotTable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initConversation = async () => {
      setIsLoading(true);
      const savedMessages = await loadConversation(charId, user);
      if (savedMessages && savedMessages.length > 0) {
        setMessages(savedMessages);
      } else {
        const randomGreeting = character.greetings[Math.floor(Math.random() * character.greetings.length)];
        setMessages([{ role: 'ai', content: randomGreeting }]);
      }
      setIsLoading(false);
    };
    initConversation();
  }, [charId, user, character]);

  useEffect(() => {
    if (messages.length > 0 && !isLoading) {
      saveConversation(charId, messages, user);
    }
  }, [messages, charId, user, isLoading]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleSend = async () => {
    if (!input.trim() || isTyping) return;
    const userMsg = { role: 'user', content: input };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setIsTyping(true);
    try {
      const aiResponseContent = await chatService.sendMessage(character, newMessages, user?.name || null);
      setMessages((prev) => [...prev, { role: 'ai', content: aiResponseContent }]);
    } catch (error) {
      console.error(error);
      setMessages((prev) => [...prev, { role: 'ai', content: '(似乎有些恍神，沒聽清楚你說什麼，能再說一次嗎?)' }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleTarotConfirm = async (card) => {
    setShowTarotTable(false);
    const categoryContext = {
      daily: '這是一張關於今日運勢的指引牌。',
      love: '這是一張關於感情關係的指引牌。',
      career: '這是一張關於工作事業的指引牌。',
      wealth: '這是一張關於財運金錢的指引牌。',
      social: '這是一張關於人際關係的指引牌。',
      custom: card.customQuestion ? `我的問題是:「${card.customQuestion}」` : '這是一張自由提問的指引牌。',
    };
    const contextPrompt = card.questionCategory ? categoryContext[card.questionCategory] : '這是一張塔羅指引牌。';
    const userMsg = {
      role: 'user',
      content: `${contextPrompt}\n我抽到了塔羅牌【${card.name}】(${card.statusText})。\n關鍵字:${card.currentMeaning.keywords.join('、')}。\n牌面含義:「${card.currentMeaning.meaning}」。\n請以占卜師的口吻,針對我的問題進行深度解讀。`,
      card: card,
    };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setIsTyping(true);
    try {
      const aiResponseContent = await chatService.sendMessage(character, newMessages, user?.name || null);
      setMessages((prev) => [...prev, { role: 'ai', content: aiResponseContent }]);
    } catch (error) {
      setMessages((prev) => [...prev, { role: 'ai', content: '(蠟燭突然熄滅了，靈感暫時中斷，請稍後再試。)' }]);
    } finally {
      setIsTyping(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-[#0a0a0c] flex items-center justify-center">
        <div className="grain-overlay" />
        <p className="text-amber-400 text-sm tracking-widest">載入對話中...</p>
      </div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, filter: 'blur(10px)' }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        className="flex flex-col h-[100vh] h-[100dvh] bg-[#0a0a0c] overflow-hidden gpu-layer relative"
      >
        <div className="grain-overlay z-10" />
        <div className="absolute top-0 inset-x-0 h-[50vh] z-0 pointer-events-none opacity-[0.15]">
          <img
            src={character.img}
            alt="background context"
            className="w-full h-full object-cover filter blur-[40px] contrast-125 sepia-[0.3] hue-rotate-[-10deg] mix-blend-screen"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0c]/20 via-[#0a0a0c]/80 to-[#0a0a0c]" />
        </div>
        <div className="vignette z-10" />

        <header className="fixed top-0 inset-x-0 z-50 bg-gradient-to-b from-[#050508]/90 to-transparent pt-5 md:pt-7 pb-8 px-5 md:px-10 pointer-events-none flex justify-between items-start safe-pt">
          <div className="w-1/3 flex justify-start">
            <button
              onClick={() => navigate('/bar')}
              className="pointer-events-auto group flex items-center space-x-2 text-gray-400 hover:text-amber-400 transition-colors duration-300"
            >
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full border border-white/10 group-hover:border-amber-400/50 flex items-center justify-center bg-white/[0.02] backdrop-blur-sm transition-all shadow-md">
                <SafeIcon icon={FiChevronLeft} className="text-sm md:text-base group-hover:-translate-x-0.5 transition-transform" />
              </div>
              <span className="text-[9px] md:text-[10px] tracking-[0.3em] uppercase hidden lg:block">Back</span>
            </button>
          </div>

          <div className="w-1/3 flex flex-col items-center pointer-events-auto mt-1 md:mt-2">
            <span className="text-[7px] md:text-[8px] text-amber-400/80 uppercase tracking-[0.4em] mb-1.5 drop-shadow-[0_0_5px_rgba(251,191,36,0.3)] whitespace-nowrap">
              Sitting with
            </span>
            <h2 className="text-sm md:text-base lg:text-lg tracking-[0.2em] text-white font-serif font-light drop-shadow-lg whitespace-nowrap">
              {character.name}
            </h2>
          </div>

          <div className="w-1/3 flex justify-end items-start flex-col space-y-2 pointer-events-auto">
            <MidnightClock />
            <div className="ml-auto">
              <span className="text-amber-500/80 font-serif tracking-[0.2em] text-xs whitespace-nowrap">
                Midnight Bar
              </span>
            </div>
          </div>
        </header>

        <QuotaIndicator character={character} />

        <main className="flex-1 overflow-y-auto px-5 md:px-10 lg:px-12 no-scrollbar z-20 relative pt-28 md:pt-36 pb-36 md:pb-48 safe-pb">
          <div className="max-w-4xl mx-auto">
            <div className="mb-12 md:mb-16 flex flex-col items-center justify-center opacity-60">
              <div className="w-px h-8 md:h-12 bg-gradient-to-b from-amber-400/60 to-transparent mb-3 md:mb-4" />
              <span className="text-[7px] md:text-[8px] tracking-[0.5em] text-amber-400/90 uppercase drop-shadow-sm text-center">
                Conversation Started
              </span>
            </div>

            <AnimatePresence mode="popLayout">
              {messages.map((msg, i) => (
                <MessageItem key={i} msg={msg} character={character} />
              ))}
              {isTyping && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="flex justify-start mb-10 md:mb-12"
                >
                  <div className="flex items-center space-x-3 md:space-x-4 pl-12 md:pl-16">
                    <span className="text-[10px] md:text-[11px] text-amber-400/70 tracking-widest font-light italic">
                      {character.action}
                    </span>
                    <div className="flex space-x-1.5 opacity-80">
                      <div className="w-1 h-1 bg-amber-400 rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
                      <div className="w-1 h-1 bg-amber-400 rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
                      <div className="w-1 h-1 bg-amber-400 rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <div ref={scrollRef} className="h-4 md:h-10" />
          </div>
        </main>

        <ChatInput
          input={input}
          setInput={setInput}
          handleSend={handleSend}
          character={character}
          onOpenTarot={() => setShowTarotTable(true)}
          isTyping={isTyping}
        />
      </motion.div>

      <AnimatePresence>
        {showTarotTable && (
          <TarotTable
            onClose={() => setShowTarotTable(false)}
            onConfirm={handleTarotConfirm}
          />
        )}
      </AnimatePresence>
    </>
  );
}